import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import RESOURCES, { thumbnailUrl } from "../data/resources";

const theme = {
  background: "#fafafa",
  text: "#333",
  textSecondary: "#555",
  textTertiary: "#666",
  textLight: "#888",
  nameColor: "#1a1a1a",
  divider: "#333",
  dividerSoft: "#d9d9d9",
  iconColor: "#333",
};

const styles = {
  page: {
    minHeight: "100vh",
    width: "100%",
    backgroundColor: theme.background,
    color: theme.text,
    fontFamily: "'Georgia', serif",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "80px 24px 96px",
    boxSizing: "border-box",
  },
  content: {
    width: "100%",
    maxWidth: "920px",
    textAlign: "center",
  },
  overline: {
    fontSize: "0.85rem",
    fontWeight: "300",
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: theme.textLight,
    marginBottom: "1rem",
  },
  title: {
    fontSize: "2.6rem",
    fontWeight: "300",
    letterSpacing: "0.03em",
    color: theme.nameColor,
    lineHeight: "1.2",
    margin: "0 0 1.25rem 0",
  },
  subtitle: {
    fontSize: "1.05rem",
    lineHeight: "1.7",
    color: theme.textSecondary,
    fontWeight: "300",
    margin: "0 auto 3rem",
    maxWidth: "560px",
  },
  grid: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: "28px",
    listStyle: "none",
    padding: 0,
    margin: "0 0 3rem 0",
    width: "100%",
  },
  backLink: {
    marginTop: "1rem",
    fontSize: "0.85rem",
    color: theme.textLight,
    textDecoration: "none",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    fontWeight: "300",
    borderBottom: `1px solid ${theme.textLight}`,
    paddingBottom: "2px",
    display: "inline-block",
    transition: "color 0.2s ease, border-color 0.2s ease",
  },
};

const cardStyles = {
  item: {
    flex: "1 1 320px",
    maxWidth: "420px",
    minWidth: 0,
    listStyle: "none",
  },
  link: {
    display: "flex",
    flexDirection: "column",
    textDecoration: "none",
    color: "inherit",
    borderRadius: "4px",
    overflow: "hidden",
    border: `1px solid ${theme.dividerSoft}`,
    backgroundColor: "#fff",
    transition:
      "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
    textAlign: "left",
    height: "100%",
  },
  thumbWrap: {
    position: "relative",
    width: "100%",
    paddingTop: "56.25%",
    backgroundColor: "#eceae6",
    overflow: "hidden",
  },
  thumb: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  thumbFallback: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.textLight,
    fontSize: "0.8rem",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    fontFamily: "'Georgia', serif",
  },
  body: {
    padding: "20px 22px 22px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    flex: 1,
  },
  kicker: {
    fontSize: "0.7rem",
    fontWeight: "600",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: theme.textLight,
    margin: 0,
  },
  title: {
    fontSize: "1.15rem",
    fontWeight: "400",
    color: theme.nameColor,
    margin: 0,
    letterSpacing: "0.01em",
    lineHeight: "1.35",
  },
  desc: {
    fontSize: "0.95rem",
    lineHeight: "1.6",
    color: theme.textSecondary,
    fontWeight: "300",
    margin: 0,
  },
};

function ResourceCard({ resource, thumbSize = 1000 }) {
  const [imgOk, setImgOk] = useState(true);
  const [hovered, setHovered] = useState(false);

  return (
    <li style={cardStyles.item}>
      <a
        href={resource.url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          ...cardStyles.link,
          transform: hovered ? "translateY(-2px)" : "translateY(0)",
          boxShadow: hovered
            ? "0 14px 32px rgba(0,0,0,0.10)"
            : "0 2px 6px rgba(0,0,0,0.04)",
          borderColor: hovered ? theme.divider : theme.dividerSoft,
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div style={cardStyles.thumbWrap}>
          {imgOk ? (
            <img
              src={thumbnailUrl(resource.slideId, thumbSize)}
              alt={resource.title}
              style={cardStyles.thumb}
              loading="lazy"
              onError={() => setImgOk(false)}
            />
          ) : (
            <div style={cardStyles.thumbFallback}>{resource.source}</div>
          )}
        </div>
        <div style={cardStyles.body}>
          <p style={cardStyles.kicker}>
            {resource.type} &middot; {resource.source}
          </p>
          <h2 style={cardStyles.title}>{resource.title}</h2>
          <p style={cardStyles.desc}>{resource.description}</p>
        </div>
      </a>
    </li>
  );
}

function Resources() {
  const [backHovered, setBackHovered] = useState(false);

  useEffect(() => {
    const html = document.documentElement;
    const prevSnap = html.style.scrollSnapType;
    html.style.scrollSnapType = "none";
    const prevTitle = document.title;
    document.title = "Resources — Daniel Sumah";
    return () => {
      html.style.scrollSnapType = prevSnap;
      document.title = prevTitle;
    };
  }, []);

  return (
    <div style={styles.page}>
      <div style={styles.content}>
        <p style={styles.overline}>Resources</p>
        <h1 style={styles.title}>Talks &amp; decks</h1>
        <p style={styles.subtitle}>
          A growing collection of decks — from talks, workshops, book
          reviews, and project work. Click any card to open the full deck.
        </p>

        <ul style={styles.grid}>
          {RESOURCES.map((r) => (
            <ResourceCard key={r.id} resource={r} thumbSize={1200} />
          ))}
        </ul>

        <Link
          to="/"
          style={{
            ...styles.backLink,
            color: backHovered ? theme.nameColor : theme.textLight,
            borderColor: backHovered ? theme.nameColor : theme.textLight,
          }}
          onMouseEnter={() => setBackHovered(true)}
          onMouseLeave={() => setBackHovered(false)}
        >
          ← Back to home
        </Link>
      </div>
    </div>
  );
}

export { ResourceCard };
export default Resources;
